/**
 * Shared focus-trap + file helpers for crop modals
 * (Adjust thumbnail, Profile picture, Cover photo).
 */

export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Whether to stamp the prior-session initialCrop onto the cropper.
 * Skip after the user picks a replacement file in this modal session —
 * those coords belong to the previous image.
 */
export function shouldApplyInitialCrop(initialCrop, userSelectedFile) {
  if (!initialCrop || userSelectedFile) {
    return false;
  }

  const { x, y, width, height } = initialCrop;
  return (
    typeof x === 'number' &&
    typeof y === 'number' &&
    typeof width === 'number' &&
    typeof height === 'number'
  );
}

/**
 * Image payload for Save: never fall back to the previous cover URL after a
 * replacement file was chosen in this session.
 */
export function resolveCropSaveImage({
  originalImage,
  initialSrc,
  userSelectedFile,
}) {
  if (userSelectedFile) {
    return originalImage || null;
  }

  return originalImage || initialSrc || null;
}

export function getFocusableElements(root) {
  if (!root) {
    return [];
  }

  return Array.from(
    root.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter(
    (el) =>
      !el.hasAttribute('disabled') &&
      el.getAttribute('aria-hidden') !== 'true' &&
      el.offsetParent !== null
  );
}

export const NUDGE_PX = 8;
export const PREVIEW_THROTTLE_MS = 80;

/**
 * Attach focus trap + arrow-key nudge while a crop dialog is open.
 * Returns a cleanup function.
 */
export function attachCropDialogChrome({
  dialogRef,
  getCropper,
  src,
  onNudge,
  previousFocusRef,
}) {
  if (typeof document === 'undefined') {
    return () => {};
  }

  previousFocusRef.current = document.activeElement;

  const focusDialog = () => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }
    const focusables = getFocusableElements(dialog);
    const target = focusables[0] || dialog;
    if (target && typeof target.focus === 'function') {
      target.focus();
    }
  };

  const timer = setTimeout(focusDialog, 50);

  const handleKeyDown = (event) => {
    if (event.key === 'Tab') {
      const dialog = dialogRef.current;
      const focusables = getFocusableElements(dialog);
      if (focusables.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !dialog.contains(active)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }

      if (active === last || !dialog.contains(active)) {
        event.preventDefault();
        first.focus();
      }
      return;
    }

    if (
      ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)
    ) {
      const cropper = getCropper && getCropper();
      if (!cropper || !src) {
        return;
      }

      const tag = (event.target && event.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        return;
      }

      event.preventDefault();
      let dx = 0;
      let dy = 0;
      if (event.key === 'ArrowLeft') dx = NUDGE_PX;
      if (event.key === 'ArrowRight') dx = -NUDGE_PX;
      if (event.key === 'ArrowUp') dy = NUDGE_PX;
      if (event.key === 'ArrowDown') dy = -NUDGE_PX;
      cropper.move(dx, dy);
      if (onNudge) {
        onNudge();
      }
    }
  };

  document.addEventListener('keydown', handleKeyDown);

  return () => {
    clearTimeout(timer);
    document.removeEventListener('keydown', handleKeyDown);

    const previous = previousFocusRef.current;
    if (
      previous &&
      typeof previous.focus === 'function' &&
      document.contains(previous)
    ) {
      previous.focus();
    }
  };
}
